/**
 * BackorderFulfillmentTab
 * -----------------------
 * Embedded inside the AdminBackordersBoard drawer. Provides the "Amazon-style"
 * milestone control center: Shipped / Delayed / Arrived buttons + tracking
 * timeline + manual tracking sync trigger.
 *
 * POST   /api/admin/backorders/{id}/milestones
 * GET    /api/admin/backorders/{id}/milestones
 * POST   /api/admin/tracking/sync-now
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, AlertTriangle, CheckCircle2, RefreshCw, ExternalLink, Loader2, MapPin, Clock, Mail, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import FulfillmentRecipientsEditor from "@/components/admin/FulfillmentRecipientsEditor";
import AutomationControls from "@/components/admin/AutomationControls";
import SupplierPoEmailDialog from "@/components/admin/SupplierPoEmailDialog";

const API = process.env.REACT_APP_BACKEND_URL;

const MILESTONE_META = {
  shipped: {
    label: "Shipped",
    icon: Truck,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    accent: "bg-emerald-600",
  },
  delayed: {
    label: "Delayed",
    icon: AlertTriangle,
    color: "text-amber-700",
    bg: "bg-amber-50",
    border: "border-amber-200",
    accent: "bg-amber-500",
  },
  arrived: {
    label: "Arrived",
    icon: CheckCircle2,
    color: "text-emerald-800",
    bg: "bg-emerald-100",
    border: "border-emerald-300",
    accent: "bg-emerald-700",
  },
  tracking_update: {
    label: "Tracking update",
    icon: MapPin,
    color: "text-sky-700",
    bg: "bg-sky-50",
    border: "border-sky-200",
    accent: "bg-sky-600",
  },
};

const COURIER_OPTIONS = ["UPS", "FedEx", "USPS", "DHL", "DHL Express", "OnTrac", "Freight", "Other"];

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * LineCoverageStrip
 * -----------------
 * At-a-glance per-line award status for multi-supplier backorders. Shows
 * every line item with a colored chip revealing:
 *   • Awaiting award (gray)          — no supplier assigned yet
 *   • Awarded to X (violet)          — one supplier selected for that line
 *   • Split N-way (amber)            — same line awarded to multiple suppliers
 *   • Shipped / Delivered (green)    — a shipment milestone covers it
 *
 * Also renders an overall progress bar: awarded lines / total lines.
 * Falls back gracefully when the BO has no line_ids (legacy single-item BOs).
 */
function LineCoverageStrip({ backorder, milestones }) {
  const items = backorder.items || [];
  const awards = backorder.awards || [];

  // Any shipment milestone signals lines are in flight. We use its presence
  // to color the whole strip's "shipped" state (per-line shipment mapping
  // isn't tracked yet — that's a follow-up).
  const shippedMilestone = (milestones || []).find(
    (m) => (m.type || "").toLowerCase() === "shipped",
  );

  // Build awards-by-line-id map. Empty item_line_ids means "all lines".
  const awardsByLine = new Map();
  const awardsForAllLines = [];
  for (const a of awards) {
    if (!a.item_line_ids || a.item_line_ids.length === 0) {
      awardsForAllLines.push(a);
    } else {
      for (const lid of a.item_line_ids) {
        if (!awardsByLine.has(lid)) awardsByLine.set(lid, []);
        awardsByLine.get(lid).push(a);
      }
    }
  }

  // Enrich each line with its coverage state.
  const rows = items.map((it, idx) => {
    const lid = it.line_id;
    const lineAwards = [
      ...(lid ? (awardsByLine.get(lid) || []) : []),
      ...awardsForAllLines,
    ];
    let state = "awaiting";
    if (shippedMilestone && lineAwards.length > 0) state = "shipped";
    else if (lineAwards.length > 1) state = "split";
    else if (lineAwards.length === 1) state = "awarded";
    return { idx, item: it, awards: lineAwards, state };
  });

  const totalLines = rows.length || 1;
  const coveredCount = rows.filter((r) => r.state !== "awaiting").length;
  const pct = Math.round((coveredCount / totalLines) * 100);

  const STATE_META = {
    awaiting: { label: "Awaiting award",  cls: "bg-slate-100 text-slate-600 border-slate-300" },
    awarded:  { label: "Awarded",         cls: "bg-violet-50 text-violet-700 border-violet-200" },
    split:    { label: "Split",           cls: "bg-amber-50 text-amber-800 border-amber-300" },
    shipped:  { label: "In flight",       cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  };

  if (items.length === 0) {
    return (
      <div className="p-3 rounded-lg border border-dashed border-slate-300 text-xs text-slate-500 text-center" data-testid="line-coverage-empty">
        No line items on this backorder yet — coverage view is hidden until items are added.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="line-coverage-strip">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Award Coverage
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Which lines have a supplier locked in vs. still open.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold text-slate-800" data-testid="line-coverage-count">
            {coveredCount} of {totalLines} line{totalLines === 1 ? "" : "s"} covered
          </div>
          <div className="text-[11px] text-slate-500">{pct}% complete</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
        <div
          className={`h-full transition-all ${pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-violet-500" : "bg-slate-300"}`}
          style={{ width: `${pct}%` }}
          data-testid="line-coverage-progress"
        />
      </div>

      {/* Per-line rows */}
      <ul className="space-y-1.5">
        {rows.map(({ idx, item, awards: la, state }) => {
          const meta = STATE_META[state];
          const qtyLabel = item.quantity != null ? `${item.quantity} ${item.uom || "Each"}` : "";
          return (
            <li
              key={idx}
              className="flex items-center gap-2 px-2.5 py-1.5 border border-slate-100 rounded hover:bg-slate-50"
              data-testid={`line-coverage-row-${idx}`}
            >
              <span className="w-6 text-center font-bold text-slate-400 text-xs">{idx + 1}</span>
              <span className="font-mono text-[10px] text-slate-500 w-24 truncate" title={item.sku || ""}>
                {item.sku || "—"}
              </span>
              <span className="flex-1 min-w-0 text-sm text-slate-800 truncate" title={item.name || ""}>
                {item.name || "(no name)"}
              </span>
              <span className="text-xs text-slate-500 font-mono w-20 text-right shrink-0">{qtyLabel}</span>
              <span
                className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0 ${meta.cls}`}
                data-testid={`line-coverage-state-${idx}`}
              >
                {state === "shipped" && <Truck className="w-2.5 h-2.5" />}
                {state === "awarded" && <CheckCircle2 className="w-2.5 h-2.5" />}
                {state === "split" && <AlertTriangle className="w-2.5 h-2.5" />}
                {state === "awaiting" && <Clock className="w-2.5 h-2.5" />}
                {meta.label}
              </span>
              <span className="w-56 text-right shrink-0 text-[11px] text-slate-600 truncate" title={la.map((a) => a.supplier_name).join(", ")}>
                {la.length === 0 ? (
                  <span className="italic text-slate-400">no supplier yet</span>
                ) : la.length === 1 ? (
                  la[0].supplier_name
                ) : (
                  <>
                    <span className="font-semibold text-amber-700">{la.length}×</span>{" "}
                    {la.map((a) => a.supplier_name).join(" · ")}
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {awardsForAllLines.length > 0 && items.some((it) => !it.line_id) && (
        <p className="mt-3 text-[10px] text-amber-700 italic" data-testid="line-coverage-legacy-warn">
          Some lines predate the per-line award system (no line_id). They fall under the &ldquo;all lines&rdquo; award by default.
        </p>
      )}
    </div>
  );
}

export default function BackorderFulfillmentTab({ backorder, token, onRefresh }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [milestones, setMilestones] = useState([]);
  const [trackingInfo, setTrackingInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // "shipped" | "delayed" | "arrived" | null
  const [posting, setPosting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Edit-shipments mode (no milestone email — just rewrites the array)
  const [editingShipments, setEditingShipments] = useState(false);
  const [editRows, setEditRows] = useState([{ courier: "UPS", tracking_number: "" }]);
  const [savingShipments, setSavingShipments] = useState(false);
  const openEditShipments = () => {
    const existing = (backorder.shipments || []).filter((s) => s.tracking_number);
    setEditRows(
      existing.length > 0
        ? existing.map((s) => ({
            id: s.id,
            courier: s.courier || "UPS",
            tracking_number: s.tracking_number || "",
          }))
        : trackingInfo.tracking_number
          ? [{ courier: trackingInfo.courier || "UPS", tracking_number: trackingInfo.tracking_number }]
          : [{ courier: "UPS", tracking_number: "" }],
    );
    setEditingShipments(true);
  };
  const setEditRow = (idx, patch) =>
    setEditRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addEditRow = () =>
    setEditRows((rows) => [...rows, { courier: "UPS", tracking_number: "" }]);
  const removeEditRow = (idx) =>
    setEditRows((rows) => (rows.length === 1 ? rows : rows.filter((_, i) => i !== idx)));
  const saveShipmentEdits = async () => {
    const clean = editRows
      .map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        courier: (r.courier || "").trim(),
        tracking_number: (r.tracking_number || "").trim(),
      }))
      .filter((r) => r.courier || r.tracking_number);
    const partial = clean.find((r) => !r.courier || !r.tracking_number);
    if (partial) {
      toast.error("Each row needs both a courier and a tracking number.");
      return;
    }
    setSavingShipments(true);
    try {
      await axios.put(
        `${API}/api/admin/backorders/${backorder.id}/shipments`,
        { shipments: clean },
        { headers },
      );
      toast.success(
        clean.length === 0
          ? "All shipment tracking cleared."
          : `${clean.length} shipment${clean.length === 1 ? "" : "s"} saved.`,
      );
      setEditingShipments(false);
      reload();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save shipments.");
    } finally {
      setSavingShipments(false);
    }
  };

  const [form, setForm] = useState({
    // legacy single fields preserved for the milestone payload
    courier: "UPS",
    tracking_number: "",
    // multi-shipment rows for the Shipped milestone (Feature C)
    shipments: [{ courier: "UPS", tracking_number: "" }],
    estimated_arrival: "",
    reason: "",
    note: "",
  });

  const reload = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/backorders/${backorder.id}/milestones`, { headers });
      setMilestones(r.data?.milestones || []);
      setTrackingInfo({
        tracking_number: r.data?.tracking_number,
        courier: r.data?.courier,
        tracking_last_status: r.data?.tracking_last_status,
        tracking_last_checked_at: r.data?.tracking_last_checked_at,
      });
    } catch (e) {
      console.error("Load milestones failed", e);
      toast.error("Could not load fulfillment timeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [backorder.id]);

  const open = (type) => {
    setDrawer(type);
    // Default each new shipment to include ALL backorder items at full qty.
    // Admin can uncheck rows or adjust qty for partial shipments.
    const bo_items = backorder.items || [];
    const seedLineItems = bo_items.map((it, i) => ({
      item_index: i,
      name: it.name || "",
      requested_qty: parseInt(it.quantity || it.qty) || 0,
      quantity_shipped: parseInt(it.quantity || it.qty) || 0,
      checked: true,
    }));

    // Pre-fill from existing shipments[] array if present, else from legacy
    // single-courier fields, else with one new shipment containing all items.
    const existing = (backorder.shipments || []).filter((s) => s.tracking_number);
    const seed = existing.length > 0
      ? existing.map((s) => ({
          courier: s.courier || "UPS",
          tracking_number: s.tracking_number || "",
          shipment_type: s.shipment_type || "auto",
          line_items: bo_items.map((it, i) => {
            const li = (s.line_items || []).find((x) => x.item_index === i || x.name === it.name);
            return {
              item_index: i,
              name: it.name || "",
              requested_qty: parseInt(it.quantity || it.qty) || 0,
              quantity_shipped: li ? (parseInt(li.quantity_shipped) || 0) : 0,
              checked: !!li,
            };
          }),
        }))
      : [{
          courier: trackingInfo.courier || "UPS",
          tracking_number: trackingInfo.tracking_number || "",
          shipment_type: "auto",
          line_items: seedLineItems,
        }];
    setForm({
      courier: trackingInfo.courier || "UPS",
      tracking_number: trackingInfo.tracking_number || "",
      shipments: seed,
      estimated_arrival: "",
      reason: "",
      note: "",
    });
  };

  const setShipment = (idx, patch) => {
    setForm((f) => ({
      ...f,
      shipments: f.shipments.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  };
  // Per-line-item update inside a specific shipment
  const setShipmentItem = (shipIdx, itemIdx, patch) => {
    setForm((f) => ({
      ...f,
      shipments: f.shipments.map((s, i) =>
        i !== shipIdx
          ? s
          : {
              ...s,
              line_items: (s.line_items || []).map((li, j) =>
                j === itemIdx ? { ...li, ...patch } : li,
              ),
            },
      ),
    }));
  };
  const addShipmentRow = () => {
    const bo_items = backorder.items || [];
    setForm((f) => ({
      ...f,
      shipments: [
        ...f.shipments,
        {
          courier: "UPS",
          tracking_number: "",
          shipment_type: "auto",
          // New shipments default to ALL items UNCHECKED (admin opts in).
          line_items: bo_items.map((it, i) => ({
            item_index: i,
            name: it.name || "",
            requested_qty: parseInt(it.quantity || it.qty) || 0,
            quantity_shipped: 0,
            checked: false,
          })),
        },
      ],
    }));
  };
  const removeShipmentRow = (idx) => {
    setForm((f) => ({
      ...f,
      shipments: f.shipments.length === 1 ? f.shipments : f.shipments.filter((_, i) => i !== idx),
    }));
  };

  // Infer "Full" vs "Partial" for a single shipment based on its checked
  // items + qty. Used for the auto badge and to seed the saved shipment_type.
  const inferShipmentType = (s) => {
    if (s.shipment_type === "partial" || s.shipment_type === "full") return s.shipment_type;
    const items = (s.line_items || []).filter((li) => li.checked && li.quantity_shipped > 0);
    if (items.length === 0) return "partial";
    const totalRequested = (backorder.items || []).reduce(
      (sum, it) => sum + (parseInt(it.quantity || it.qty) || 0), 0,
    );
    const totalShipped = items.reduce((sum, li) => sum + (parseInt(li.quantity_shipped) || 0), 0);
    if (totalShipped >= totalRequested && totalRequested > 0) return "full";
    return "partial";
  };

  const submit = async () => {
    if (!drawer) return;
    if (drawer === "shipped") {
      const valid = form.shipments.filter((s) => (s.tracking_number || "").trim());
      if (valid.length === 0) {
        toast.error("Add at least one shipment with a tracking number.");
        return;
      }
      const missingCarrier = valid.find((s) => !(s.courier || "").trim());
      if (missingCarrier) {
        toast.error("Pick a courier for each shipment.");
        return;
      }
    }
    if (drawer === "delayed" && !form.estimated_arrival.trim()) {
      toast.error("Please provide an updated ETA");
      return;
    }
    setPosting(true);
    try {
      if (drawer === "shipped") {
        // 1. Save the shipments[] array on the backorder doc with full
        //    per-line items + shipment_type so the backend can auto-flip
        //    status to shipped / partially_shipped.
        const cleanShipments = form.shipments
          .filter((s) => (s.tracking_number || "").trim())
          .map((s) => ({
            courier: s.courier.trim(),
            tracking_number: s.tracking_number.trim(),
            shipment_type: inferShipmentType(s),
            line_items: (s.line_items || [])
              .filter((li) => li.checked && (parseInt(li.quantity_shipped) || 0) > 0)
              .map((li) => ({
                item_index: li.item_index,
                name: li.name,
                quantity_shipped: parseInt(li.quantity_shipped) || 0,
              })),
          }));
        await axios.put(
          `${API}/api/admin/backorders/${backorder.id}/shipments`,
          { shipments: cleanShipments },
          { headers },
        );
        // 2. Post the Shipped milestone using the FIRST shipment so existing
        //    email + cron sync logic keeps working unchanged.
        const first = cleanShipments[0];
        const payload = {
          type: "shipped",
          courier: first.courier,
          tracking_number: first.tracking_number,
          note: form.note,
        };
        await axios.post(`${API}/api/admin/backorders/${backorder.id}/milestones`, payload, { headers });
        const anyPartial = cleanShipments.some((s) => s.shipment_type === "partial");
        toast.success(
          cleanShipments.length === 1
            ? (anyPartial ? "Partial shipment logged — status now Partially Shipped" : "Shipped milestone posted — hospital notified")
            : `${cleanShipments.length} shipments logged · primary tracking sent to hospital`,
        );
      } else {
        const payload = { type: drawer, ...form };
        delete payload.shipments;
        await axios.post(`${API}/api/admin/backorders/${backorder.id}/milestones`, payload, { headers });
        toast.success(`${MILESTONE_META[drawer].label} milestone posted — hospital notified`);
      }
      setDrawer(null);
      reload();
      await onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post milestone");
    } finally {
      setPosting(false);
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/api/admin/tracking/sync-now`, {}, { headers });
      const d = r.data || {};
      toast.success(
        `Sync complete — ${d.checked || 0} eligible shipment${d.checked === 1 ? "" : "s"} checked`,
        {
          description:
            d.provider === "aftership"
              ? `${d.delivery_notices_sent || 0} delivery email(s) sent · ${d.skipped_not_eligible || 0} skipped (pre-automation)`
              : "Live carrier lookup is OFF — add an AFTERSHIP_API_KEY to auto-detect delivery.",
        },
      );
      reload();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // One-click "Order Acknowledgment" — reuses the existing PO ack email
  // endpoint with the values already saved on the backorder doc. Lives here
  // in Fulfillment so it sits naturally before the Shipped milestone in the
  // delivery workflow. Disabled until a PO is saved on Details.
  const [sendingAck, setSendingAck] = useState(false);
  const [poEmailOpen, setPoEmailOpen] = useState(false);
  const ackPoNumber =
    (backorder.customer_order?.po_number || "").trim() ||
    (backorder.po_number || "").trim();
  const ackLeadCode = backorder.po_acknowledgment_lead_code || "3-5-business-days";
  const ackLeadLabel = backorder.po_acknowledgment_lead_time || "3-5 Business Days";
  const ackAlreadySent = !!backorder.po_acknowledgment_sent_at;
  const sendOrderAcknowledgment = async () => {
    if (!ackPoNumber) {
      toast.error("Save a PO number in the Details tab first.");
      return;
    }
    setSendingAck(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/email-po-acknowledgment`,
        {
          po_number: ackPoNumber,
          lead_time_code: ackLeadCode,
          lead_time_custom: ackLeadCode === "custom" ? ackLeadLabel : null,
          additional_emails: [],
        },
        { headers },
      );
      toast.success(`Order acknowledgment sent`, {
        description: `To: ${r.data?.recipients?.to} · Lead time: ${r.data?.lead_time}`,
      });
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send acknowledgment.");
    } finally {
      setSendingAck(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-fulfillment-tab">
      {/* Top action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
        <div>
          <h3 className="text-sm font-bold text-slate-800 inline-flex items-center gap-2">
            <Truck className="w-4 h-4 text-slate-600" />
            Fulfillment Control Center
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Tracking is checked <strong>once daily at 8:00 AM CT</strong>, and only for shipments entered
            after 25 Sep 2026. The hospital is emailed on <strong>ship, delay and delivery only</strong> —
            in-transit scans are logged to the timeline, never emailed. Past orders are never touched.
          </p>
          <p className="text-xs mt-1" data-testid="fulfillment-po-line">
            {ackPoNumber ? (
              <span className="font-bold text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5">
                PO #{ackPoNumber}
              </span>
            ) : (
              <span className="text-amber-700">
                No customer PO yet — add it in the <strong>Build Order</strong> tab so it appears on every email.
              </span>
            )}{" "}
            <span className="text-slate-400 ml-2">goes in the subject line and body of every fulfillment email.</span>
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 self-start sm:self-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPoEmailOpen(true)}
          data-testid="supplier-po-email-btn"
          className="border-indigo-300 text-indigo-800 hover:bg-indigo-50"
        >
          <FileText className="w-3.5 h-3.5 mr-1.5" />
          Send Supplier PO to Supplier
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={runSync}
          disabled={syncing}
          data-testid="admin-tracking-sync-btn"
          className="self-start sm:self-auto"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Run Tracking Sync Now
        </Button>
        </div>
      </div>

      <SupplierPoEmailDialog
        open={poEmailOpen}
        onOpenChange={setPoEmailOpen}
        backorderId={backorder.id}
        token={token}
        onLogged={onRefresh}
      />

      {/* Global automation kill switch + one-time cleanup */}
      <AutomationControls token={token} />

      {/* Who gets the fulfillment emails */}
      <FulfillmentRecipientsEditor
        backorderId={backorder.id}
        token={token}
        onSaved={onRefresh}
      />

      {/* Per-line award coverage view (multi-supplier split view) */}
      <LineCoverageStrip backorder={backorder} milestones={milestones} />

      {/* Milestone buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={sendOrderAcknowledgment}
          disabled={!ackPoNumber || sendingAck}
          className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-300 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-50 disabled:hover:border-blue-200 relative"
          data-testid="milestone-order-ack-btn"
          title={
            !ackPoNumber
              ? "Add a customer PO number in the Build Order tab to unlock this."
              : ackAlreadySent
                ? `Last sent ${new Date(backorder.po_acknowledgment_sent_at).toLocaleString()} · Click to re-send.`
                : "Send the 'Your order has been received and is being processed' email to the hospital."
          }
        >
          {sendingAck ? (
            <Loader2 className="w-5 h-5 text-blue-700 mb-2 animate-spin" />
          ) : (
            <Mail className="w-5 h-5 text-blue-700 mb-2" />
          )}
          <div className="font-bold text-blue-800 text-sm">
            {ackAlreadySent ? "Re-send Order Ack" : "Order Acknowledgment"}
          </div>
          <div className="text-xs text-blue-700 mt-0.5">
            {!ackPoNumber
              ? "PO required — add it in Build Order"
              : `"Processing" email · ${ackLeadLabel}`}
          </div>
          {ackAlreadySent && !sendingAck && (
            <span className="absolute top-2 right-2 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1.5 py-0.5">
              <CheckCircle2 className="w-2.5 h-2.5" /> Sent
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={() => open("shipped")}
          className="p-4 rounded-lg border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-300 transition-all text-left group"
          data-testid="milestone-shipped-btn"
        >
          <Truck className="w-5 h-5 text-emerald-700 mb-2" />
          <div className="font-bold text-emerald-800 text-sm">Order Shipped</div>
          <div className="text-xs text-emerald-700 mt-0.5">Add courier + tracking #</div>
        </button>
        <button
          type="button"
          onClick={() => open("delayed")}
          className="p-4 rounded-lg border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-300 transition-all text-left"
          data-testid="milestone-delayed-btn"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 mb-2" />
          <div className="font-bold text-amber-800 text-sm">Order Delayed</div>
          <div className="text-xs text-amber-700 mt-0.5">New ETA + reason</div>
        </button>
        <button
          type="button"
          onClick={() => open("arrived")}
          className="p-4 rounded-lg border-2 border-emerald-300 bg-emerald-100 hover:bg-emerald-200 hover:border-emerald-400 transition-all text-left"
          data-testid="milestone-arrived-btn"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-800 mb-2" />
          <div className="font-bold text-emerald-900 text-sm">Order Arrived</div>
          <div className="text-xs text-emerald-800 mt-0.5">Finalize delivery</div>
        </button>
      </div>

      {/* Drawer for milestone form */}
      {drawer && (
        <div className={`rounded-lg border-2 ${MILESTONE_META[drawer].border} ${MILESTONE_META[drawer].bg} p-4 space-y-3`} data-testid={`milestone-form-${drawer}`}>
          <div className="flex items-center justify-between">
            <h4 className={`text-sm font-bold ${MILESTONE_META[drawer].color}`}>
              {MILESTONE_META[drawer].label} milestone
            </h4>
            <button onClick={() => setDrawer(null)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
          </div>

          {drawer === "shipped" && (
            <div className="space-y-3" data-testid="milestone-shipments-list">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-600">Shipments</label>
                <span className="text-[10px] text-slate-500">
                  Check the items in each shipment + adjust qty for partial sends
                </span>
              </div>
              {form.shipments.map((s, idx) => {
                const inferredType = inferShipmentType(s);
                return (
                  <div key={idx} className="bg-white border-2 border-slate-200 rounded p-3 space-y-2" data-testid={`shipment-row-${idx}`}>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <select
                        value={s.courier}
                        onChange={(e) => setShipment(idx, { courier: e.target.value })}
                        className="col-span-4 text-sm rounded border border-slate-300 px-2 py-1.5 bg-white"
                        data-testid={`shipment-courier-${idx}`}
                      >
                        {COURIER_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <Input
                        value={s.tracking_number}
                        onChange={(e) => setShipment(idx, { tracking_number: e.target.value })}
                        placeholder="Tracking #"
                        className="col-span-7 text-sm"
                        data-testid={`shipment-tracking-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeShipmentRow(idx)}
                        disabled={form.shipments.length === 1}
                        className="col-span-1 text-rose-600 hover:bg-rose-50 disabled:text-slate-300 disabled:hover:bg-transparent rounded p-1 inline-flex justify-center"
                        title="Remove this shipment line"
                        data-testid={`shipment-remove-${idx}`}
                      >
                        ×
                      </button>
                    </div>

                    {/* Per-line item picker (Feature 2) */}
                    {(backorder.items || []).length > 0 && (
                      <div className="rounded border border-slate-200 bg-slate-50 p-2 space-y-1" data-testid={`shipment-items-${idx}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                          Items in this shipment
                        </div>
                        {(s.line_items || []).map((li, j) => (
                          <label key={j} className="grid grid-cols-12 gap-2 items-center text-sm hover:bg-white rounded px-1 py-0.5" data-testid={`shipment-${idx}-item-${j}`}>
                            <input
                              type="checkbox"
                              checked={!!li.checked}
                              onChange={(e) => setShipmentItem(idx, j, { checked: e.target.checked, quantity_shipped: e.target.checked ? (li.requested_qty || 1) : 0 })}
                              className="col-span-1 accent-emerald-600"
                              data-testid={`shipment-${idx}-item-${j}-check`}
                            />
                            <span className="col-span-7 text-slate-700 truncate" title={li.name}>{li.name || `Item ${j + 1}`}</span>
                            <div className="col-span-4 flex items-center gap-1 text-xs text-slate-500">
                              <Input
                                type="number"
                                min="0"
                                max={li.requested_qty}
                                disabled={!li.checked}
                                value={li.quantity_shipped}
                                onChange={(e) => setShipmentItem(idx, j, { quantity_shipped: parseInt(e.target.value) || 0 })}
                                className="h-7 text-sm text-right disabled:bg-slate-100"
                                data-testid={`shipment-${idx}-item-${j}-qty`}
                              />
                              <span className="whitespace-nowrap">/ {li.requested_qty}</span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}

                    {/* Shipment type radio (Feature 2 — admin can override the auto inference) */}
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-semibold text-slate-600">Type:</span>
                      {["auto", "partial", "full"].map((t) => (
                        <label key={t} className="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            checked={(s.shipment_type || "auto") === t}
                            onChange={() => setShipment(idx, { shipment_type: t })}
                            className="accent-emerald-600"
                            data-testid={`shipment-${idx}-type-${t}`}
                          />
                          <span className={`capitalize ${(s.shipment_type || "auto") === t ? "font-bold text-slate-800" : "text-slate-500"}`}>
                            {t}
                          </span>
                        </label>
                      ))}
                      {(s.shipment_type === "auto" || !s.shipment_type) && (
                        <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          inferredType === "full"
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            : "bg-amber-100 text-amber-800 border border-amber-200"
                        }`}>
                          auto → {inferredType}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={addShipmentRow}
                className="text-xs font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
                data-testid="shipment-add-row-btn"
              >
                + Add Another Shipment Line
              </button>
            </div>
          )}

          {drawer === "delayed" && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Updated ETA *</label>
                <Input
                  type="date"
                  value={form.estimated_arrival}
                  onChange={(e) => setForm({ ...form, estimated_arrival: e.target.value })}
                  className="text-sm"
                  data-testid="milestone-eta-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Reason for delay *</label>
                <Textarea
                  rows={2}
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  placeholder="e.g. Backorder at supplier — manufacturer constrained until..."
                  className="text-sm"
                  data-testid="milestone-reason-input"
                />
              </div>
            </div>
          )}

          {drawer !== "delayed" && (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Optional note to hospital</label>
              <Textarea
                rows={2}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Any extra detail for the hospital email..."
                className="text-sm"
                data-testid="milestone-note-input"
              />
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDrawer(null)}
              disabled={posting}
            >Cancel</Button>
            <Button
              size="sm"
              onClick={submit}
              disabled={posting}
              className={`text-white ${MILESTONE_META[drawer].accent} hover:opacity-90`}
              data-testid="milestone-submit-btn"
            >
              {posting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
              Post milestone & email hospital
            </Button>
          </div>
        </div>
      )}

      {/* Edit shipments inline editor (Feature C — edit) */}
      {editingShipments && (
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 space-y-2" data-testid="edit-shipments-form">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-bold text-emerald-800">Edit shipments</h4>
            <button onClick={() => setEditingShipments(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
          </div>
          <p className="text-[11px] text-slate-600">
            Update or replace your carrier &amp; tracking lines. Saving here does <strong>not</strong> re-email the hospital — use the Shipped button if you want to fire a fresh notification.
          </p>
          {editRows.map((s, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-white border border-slate-200 rounded p-2" data-testid={`edit-shipment-row-${idx}`}>
              <select
                value={s.courier}
                onChange={(e) => setEditRow(idx, { courier: e.target.value })}
                className="col-span-4 text-sm rounded border border-slate-300 px-2 py-1.5 bg-white"
                data-testid={`edit-shipment-courier-${idx}`}
              >
                {COURIER_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Input
                value={s.tracking_number}
                onChange={(e) => setEditRow(idx, { tracking_number: e.target.value })}
                placeholder="Tracking #"
                className="col-span-7 text-sm"
                data-testid={`edit-shipment-tracking-${idx}`}
              />
              <button
                type="button"
                onClick={() => removeEditRow(idx)}
                disabled={editRows.length === 1}
                className="col-span-1 text-rose-600 hover:bg-rose-50 disabled:text-slate-300 disabled:hover:bg-transparent rounded p-1 inline-flex justify-center"
                title="Remove this shipment line"
                data-testid={`edit-shipment-remove-${idx}`}
              >
                ×
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={addEditRow}
              className="text-xs font-bold text-emerald-700 hover:text-emerald-800"
              data-testid="edit-shipment-add-btn"
            >
              + Add Another Shipment Line
            </button>
            <Button
              size="sm"
              onClick={saveShipmentEdits}
              disabled={savingShipments}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 text-xs"
              data-testid="edit-shipment-save-btn"
            >
              {savingShipments ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Save shipments
            </Button>
          </div>
        </div>
      )}

      {/* Multi-shipment summary (Feature C) — only renders when >1 shipment */}
      {(backorder.shipments || []).length > 1 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="admin-multi-shipments-summary">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5" />
              {backorder.shipments.length} active shipments
            </div>
            <button
              type="button"
              onClick={openEditShipments}
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
              data-testid="edit-shipments-btn"
            >
              ✏ Edit
            </button>
          </div>
          <div className="space-y-1">
            {backorder.shipments.map((s, i) => (
              <div key={s.id || i} className="flex items-center justify-between text-xs bg-white rounded border border-emerald-100 px-2 py-1.5">
                <span className="font-bold text-slate-700 w-20">{s.courier}</span>
                <span className="font-mono text-slate-800 flex-1 mx-2 truncate">{s.tracking_number}</span>
                {s.tracking_url && (
                  <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1">
                    Track <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tracking status pill (if any) */}
      {trackingInfo.tracking_number && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="admin-tracking-status">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-50 border border-sky-200 flex items-center justify-center">
              <Truck className="w-4 h-4 text-sky-600" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{trackingInfo.courier}</div>
              <div className="text-sm font-mono font-bold text-slate-800">{trackingInfo.tracking_number}</div>
              {trackingInfo.tracking_last_status && (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Latest: <span className="font-semibold text-slate-700">{trackingInfo.tracking_last_status}</span>
                  {trackingInfo.tracking_last_checked_at && <> · checked {fmtTs(trackingInfo.tracking_last_checked_at)}</>}
                </div>
              )}
            </div>
          </div>
          {(backorder.shipments || []).length <= 1 && (
            <button
              type="button"
              onClick={openEditShipments}
              className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 self-start sm:self-auto"
              data-testid="edit-tracking-single-btn"
            >
              ✏ Edit tracking
            </button>
          )}
        </div>
      )}

      {/* Timeline */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Fulfillment Timeline</h4>
          <span className="text-[11px] text-slate-400">{milestones.length} milestone{milestones.length === 1 ? "" : "s"}</span>
        </div>
        {loading ? (
          <div className="text-sm text-slate-500 py-8 text-center">Loading...</div>
        ) : milestones.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-lg">
            No milestones yet. Use the buttons above to push the first update to the hospital.
          </div>
        ) : (
          <ol className="space-y-2" data-testid="milestone-timeline">
            {[...milestones].reverse().map((m) => {
              const meta = MILESTONE_META[m.type] || MILESTONE_META.tracking_update;
              const Icon = meta.icon;
              return (
                <li
                  key={m.id || m.ts}
                  className={`flex gap-3 p-3 rounded-lg border ${meta.border} ${meta.bg}`}
                  data-testid={`milestone-item-${m.type}`}
                >
                  <div className={`shrink-0 w-8 h-8 rounded-full ${meta.accent} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-bold ${meta.color}`}>{meta.label}</span>
                      <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fmtTs(m.ts)}
                      </span>
                      <span className="text-[11px] text-slate-400">by {m.by || "admin"}</span>
                    </div>
                    {m.tracking_number && (
                      <div className="text-xs text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold">{m.courier || ""} · {m.tracking_number}</span>
                        {m.tracking_url && (
                          <a href={m.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-medium">
                            Track <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    )}
                    {m.estimated_arrival && (
                      <div className="text-xs text-amber-700 mt-1"><strong>New ETA:</strong> {m.estimated_arrival}</div>
                    )}
                    {m.reason && (
                      <div className="text-xs text-amber-700 mt-1"><strong>Reason:</strong> {m.reason}</div>
                    )}
                    {m.current_tracking_status && (
                      <div className="text-xs text-sky-700 mt-1"><strong>Status:</strong> {m.current_tracking_status}</div>
                    )}
                    {m.note && (
                      <div className="text-xs text-slate-600 mt-1 italic">"{m.note}"</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
